import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, ShieldCheck, MessageSquare } from "lucide-react";
import { ORDER } from "@/constants/testIds";
import { useOrder } from "@/store/orderStore";
import { useTenant } from "@/tenant/TenantProvider";
import { cleanOptions, formatPrice, lineItemPrice } from "@/lib/format";
import { orderErrorMessage } from "@/lib/serverMessages";
import { fetchJson } from "@/lib/api";
import { EVENTS, track } from "@/lib/analytics";
import { pendingOrderKey } from "@/lib/storageKeys";
import { useAuth } from "@/auth/AuthProvider";
import ProfilePrompt from "@/components/ProfilePrompt";

const Confirmation = ({ onPay, onNewOrder }) => {
  const { state, setOrderResult, setGuest } = useOrder();
  const { slug, restaurant, findDish } = useTenant();
  const { user } = useAuth();
  const { t } = useTranslation();
  const order = state.lastOrder;
  // Server response: the real prices. Seeded from the persisted order so a
  // remount (refresh, history Forward) re-shows the receipt instead of
  // re-submitting an order the kitchen already has.
  const [placed, setPlaced] = useState(order?.server || null);
  const [placeError, setPlaceError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [profileDismissed, setProfileDismissed] = useState(false);
  const submittingRef = useRef(false);

  // Persist the order to backend on mount (anonymous or authed). Guarded to run once.
  // The payload carries no money: the server prices the order from its own menu.
  useEffect(() => {
    if (!order || placed || submittingRef.current) return;
    // The ref (not a cleanup flag) guards the double-invoke: in StrictMode the
    // effect is torn down and re-run, and abandoning the in-flight POST would
    // throw away the server's prices for an order that was really placed.
    submittingRef.current = true;
    setPlaceError(null);
    const payload = {
      restaurant_slug: slug,
      table: String(order.guest.table || ""),
      // When present the server takes the table from this signed token, not from
      // the field above.
      table_token: order.guest.tableToken || null,
      guest_name: order.guest.name || null,
      allergy_note: order.guest.allergy || null,
      no_seasoning: !!order.guest.noSeasoning,
      served: order.guest.served || "plate",
      fulfillment: order.guest.fulfillment === "takeaway" ? "takeaway" : "dine_in",
      order_note: order.orderNote || null,
      ai_request_id: order.aiRequestId || null,
      reorder_of: order.reorderOf || null,
      items: order.items.map((it) => ({
        dish_id: it.dishId,
        qty: it.qty,
        options: cleanOptions(it.options),
        source: it.source === "ai" ? "ai" : "manual",
      })),
    };
    (async () => {
      try {
        const data = await fetchJson("/orders", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        if (!data) return;
        setPlaced(data);
        setOrderResult(data);
        const key = pendingOrderKey(slug);
        if (!user && data.order_id) {
          // Remember for post-signup attach; the claim token proves it is ours.
          localStorage.setItem(
            key,
            JSON.stringify({ orderId: data.order_id, claimToken: data.claim_token })
          );
        } else {
          localStorage.removeItem(key);
        }
      } catch (e) {
        // The kitchen can stop-list a dish while the guest is deciding (409), so
        // failure must be visible — the waiter has no ticket for this order.
        submittingRef.current = false;
        // The one funnel step the server cannot report, because there is no
        // order to report it against: this is the guest who tapped Place order
        // and got nothing. `attempt` says whether they had already tried.
        track(EVENTS.ORDER_FAILED, {
          reason: e.status ? `http_${e.status}` : "network",
          attempt: attempt + 1,
          lines: order.items.length,
        });
        if (e.status === 401) {
          // The dine-in session ran out between building the plate and paying.
          // Clearing it hands the guest to the scan screen, which says what to do;
          // leaving it would loop them through a checkout that cannot succeed.
          setGuest({ tableLocked: false, tableToken: null, tableSessionExpiresAt: null });
          return;
        }
        // The error itself, not a sentence: it is turned into the guest's
        // language when it is drawn, so switching language redraws it.
        setPlaceError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, placed, user, slug, attempt]);

  if (!order) {
    return (
      <div className="px-5 py-20 text-center">
        <p className="text-neutral-500">{t("confirm.noOrder")}</p>
        <button
          data-testid="confirm-new-order"
          onClick={onNewOrder}
          className="mt-6 px-6 min-h-12 py-2 rounded-2xl bg-neutral-900 text-white font-bold text-sm"
        >
          {t("confirm.startNew")}
        </button>
      </div>
    );
  }

  return (
    <div className="px-5 pt-10 pb-12">
      <div className="flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-full bg-[#22C55E]/10 flex items-center justify-center mb-5">
          <div className="w-14 h-14 rounded-full bg-[#22C55E] flex items-center justify-center">
            <Check size={32} className="text-white" strokeWidth={3} />
          </div>
        </div>
        <h1 data-testid="confirm-title" className="text-[34px] leading-[1.05] font-black tracking-tight text-neutral-900">
          {t("confirm.title")}
        </h1>
        <p className="text-sm text-neutral-500 max-w-[32ch] mt-2">{t("confirm.subtitle")}</p>
      </div>

      {placeError && (
        <div
          data-testid={ORDER.error}
          className="mt-5 flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4"
        >
          <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-neutral-900">{t("confirm.errorTitle")}</div>
            <div className="text-xs text-neutral-600 mt-0.5">
              {orderErrorMessage(t, placeError)}
            </div>
            <button
              data-testid={ORDER.retryButton}
              onClick={() => setAttempt((a) => a + 1)}
              className="mt-3 min-h-10 py-2 px-4 rounded-xl bg-neutral-900 text-white text-xs font-bold active:scale-[0.98] transition-transform"
            >
              {t("common.tryAgain")}
            </button>
          </div>
        </div>
      )}

      {user && (
        <div
          data-testid="order-saved-note"
          className="mt-5 flex items-center gap-3 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-2xl p-4"
        >
          <ShieldCheck size={20} className="text-[#22C55E] shrink-0" />
          <div>
            <div className="text-sm font-bold text-neutral-900">{t("confirm.savedTitle")}</div>
            <div className="text-xs text-neutral-500">{t("confirm.savedBody")}</div>
          </div>
        </div>
      )}

      <div className="mt-5 bg-white border border-neutral-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              {order.guest.fulfillment === "takeaway" ? t("confirm.takeaway") : t("confirm.table")}
            </div>
            <div className="text-lg font-black text-neutral-900" data-testid="confirm-fulfillment">
              {order.guest.fulfillment === "takeaway"
                ? t("confirm.atCounter")
                : `#${order.guest.table}`}
            </div>
          </div>
          <div className="text-right min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              {t("common.guest")}
            </div>
            <div className="text-sm font-bold text-neutral-900 truncate">
              {order.guest.name || t("common.guest")}
            </div>
          </div>
        </div>
        <div className="h-px bg-neutral-100 my-3" />
        {/* Lines and prices as the server booked them; the local cart is only a
            fallback for the moment before the response lands. */}
        <ul className="space-y-1.5" data-testid="confirm-items">
          {(placed?.items || order.items).map((it, idx) => {
            const name = it.name || findDish(it.dishId)?.name;
            if (!name) return null;
            const price =
              it.price !== undefined
                ? it.price
                : lineItemPrice(findDish(it.dishId), it.options, it.qty);
            return (
              <li key={it.uid || `${it.dish_id}-${idx}`} className="flex items-center justify-between text-sm">
                <span className="text-neutral-700">
                  {name} <span className="text-neutral-400">×{it.qty}</span>
                </span>
                <span className="font-semibold text-neutral-900">{formatPrice(price)}</span>
              </li>
            );
          })}
        </ul>
        {placed && (
          <>
            <div className="h-px bg-neutral-100 my-3" />
            <div className="space-y-1 text-sm">
              <SummaryRow label={t("confirm.subtotal")} value={placed.subtotal} testId="confirm-subtotal" />
              <SummaryRow label={t("confirm.tax")} value={placed.tax} testId="confirm-tax" />
              <SummaryRow label={t("confirm.service")} value={placed.service} testId="confirm-service" />
            </div>
          </>
        )}
        {order.orderNote && (
          <>
            <div className="h-px bg-neutral-100 my-3" />
            <div data-testid="confirm-note" className="flex items-start gap-2 text-xs">
              <MessageSquare size={12} className="text-neutral-400 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold uppercase tracking-wider text-neutral-500 text-[10px] mb-0.5">
                  {t("confirm.noteTitle")}
                </div>
                <div className="text-neutral-700 italic">{order.orderNote}</div>
              </div>
            </div>
          </>
        )}
        <div className="h-px bg-neutral-100 my-3" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-bold uppercase tracking-wider text-neutral-700">
            {t("confirm.totalOnBill")}
          </span>
          <span data-testid="confirm-total" className="text-xl font-black text-neutral-900 shrink-0">
            {formatPrice(placed ? placed.grand : order.grand)}
          </span>
        </div>
      </div>

      <button
        data-testid="pay-now-btn"
        onClick={() => {
          // Payment is a mock and no order carries a method, so this counts taps
          // on a button, not money — `mock: true` is in the event so nobody
          // builds a revenue chart on it by mistake.
          track(EVENTS.PAYMENT_COMPLETED, { mock: true, grand: placed?.grand });
          onPay();
        }}
        className="mt-5 w-full h-16 px-4 rounded-2xl bg-neutral-900 text-white font-bold text-lg tracking-wide active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
      >
        {t("confirm.payNow")}
      </button>

      {!user && !profileDismissed && (
        <div className="mt-5">
          <ProfilePrompt onLater={() => setProfileDismissed(true)} />
        </div>
      )}

      <div className="mt-8 text-[10px] text-neutral-400 text-center">
        {restaurant.name}
        {restaurant.branding?.location ? ` · ${restaurant.branding.location}` : ""}
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, testId }) => (
  <div className="flex items-center justify-between">
    <span className="text-neutral-500">{label}</span>
    <span data-testid={testId} className="font-semibold text-neutral-900">
      {formatPrice(value)}
    </span>
  </div>
);

export default Confirmation;
